function Hero() {
  return (
    <section className="relative pt-24 lg:pt-32 pb-16 lg:pb-24 bg-gradient-hero overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 grass-pattern opacity-50" />
      <div className="absolute top-20 right-0 w-96 h-96 bg-grass-200 rounded-full blur-3xl opacity-30" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-earth-200 rounded-full blur-3xl opacity-30" />

      <div className="container-custom px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left column - Text content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-grass-100 rounded-full text-grass-700 font-medium text-sm mb-6">
              <span className="w-2 h-2 bg-grass-500 rounded-full animate-pulse" />
              Coming Soon
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-6">
              Your Dream Lawn,{' '}
              <span className="gradient-text">Powered by AI</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
              Get personalized care plans, smart watering schedules, and expert
              AI guidance to transform your yard into the best lawn on the
              block.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <a href="#waitlist" className="btn-accent text-lg px-8 py-4">
                Get Early Access
                <svg
                  className="w-5 h-5 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </a>
              <a href="#features" className="btn-secondary text-lg px-8 py-4">
                See Features
              </a>
            </div>

            {/* Trust indicators */}
            <div className="mt-10 flex flex-wrap items-center justify-center lg:justify-start gap-6 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-grass-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                Personalized for your lawn
              </div>
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-grass-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                AI-powered advice
              </div>
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-grass-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                iOS & Android
              </div>
            </div>
          </div>

          {/* Right column - Hero image/illustration */}
          <div className="relative">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              {/* Phone mockup */}
              <div className="relative z-10 bg-gray-900 rounded-[3rem] p-3 shadow-2xl transform rotate-2 hover:rotate-0 transition-transform duration-500">
                <div className="bg-gray-800 rounded-[2.5rem] overflow-hidden">
                  {/* Phone screen - scaled 2x */}
                  <div
                    className="aspect-[9/19] bg-gray-50 relative overflow-hidden flex flex-col"
                    style={{ fontSize: '200%' }}
                  >
                    {/* Status bar - iOS style */}
                    <div className="bg-grass-600 flex justify-between items-center px-8 pt-4 pb-2 text-sm text-white/90">
                      <span className="font-semibold">9:41</span>
                      <div className="absolute left-1/2 -translate-x-1/2 w-32 h-8 bg-black rounded-b-3xl" />
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M12 3a9 9 0 019 9h-2a7 7 0 00-7-7V3zm0 4a5 5 0 015 5h-2a3 3 0 00-3-3V7z" />
                        </svg>
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3a4.237 4.237 0 00-6 0zm-4-4l2 2a7.074 7.074 0 0110 0l2-2C15.14 9.14 8.87 9.14 5 13z" />
                        </svg>
                        <div className="w-8 h-4 bg-white/90 rounded-sm relative">
                          <div
                            className="absolute inset-0.5 bg-grass-400 rounded-sm"
                            style={{ width: '85%' }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Header with lawn photo */}
                    <div className="bg-gradient-to-b from-grass-600 to-grass-500 px-5 pt-3 pb-5">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <div className="text-white/70 text-xs font-medium tracking-wide">
                            GOOD MORNING, ALEX
                          </div>
                          <div className="text-white text-xl font-bold">
                            My Front Yard
                          </div>
                        </div>
                        <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                          <svg
                            className="w-6 h-6 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                            />
                          </svg>
                        </div>
                      </div>
                      {/* Lawn photo preview */}
                      <div className="relative rounded-2xl overflow-hidden h-28 bg-gradient-to-br from-grass-400 to-grass-600">
                        {/* SVG lawn illustration */}
                        <svg
                          className="absolute inset-0 w-full h-full"
                          viewBox="0 0 200 80"
                          preserveAspectRatio="xMidYMid slice"
                        >
                          <defs>
                            <linearGradient
                              id="lawnGrad"
                              x1="0%"
                              y1="0%"
                              x2="0%"
                              y2="100%"
                            >
                              <stop offset="0%" stopColor="#86efac" />
                              <stop offset="100%" stopColor="#22c55e" />
                            </linearGradient>
                          </defs>
                          <rect fill="url(#lawnGrad)" width="200" height="80" />
                          {/* Grass blade pattern */}
                          {[...Array(40)].map((_, i) => (
                            <path
                              key={i}
                              d={`M${5 + i * 5} 80 Q${3 + i * 5} ${50 + (i % 3) * 8} ${5 + i * 5} ${40 + (i % 4) * 5}`}
                              stroke="#16a34a"
                              strokeWidth="1.5"
                              fill="none"
                              opacity={0.6 + (i % 3) * 0.15}
                            />
                          ))}
                        </svg>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                        <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 bg-white/90 rounded-full flex items-center justify-center">
                              <svg
                                className="w-4 h-4 text-grass-600"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                            <span className="text-xs text-white font-medium">
                              Updated 2h ago
                            </span>
                          </div>
                          <div className="px-3 py-1 bg-grass-500 rounded-full text-xs text-white font-semibold">
                            2,400 sq ft
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Main scrollable content */}
                    <div className="flex-1 px-4 py-4 overflow-hidden">
                      {/* Health Score Card */}
                      <div className="bg-white rounded-2xl shadow-lg p-4 mb-4">
                        <div className="flex items-center gap-4">
                          <div className="relative w-20 h-20 flex-shrink-0">
                            <svg
                              className="w-20 h-20 -rotate-90"
                              viewBox="0 0 36 36"
                            >
                              <path
                                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                fill="none"
                                stroke="#e5e7eb"
                                strokeWidth="3"
                              />
                              <path
                                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                fill="none"
                                stroke="url(#scoreGradient)"
                                strokeWidth="3"
                                strokeDasharray="87, 100"
                                strokeLinecap="round"
                              />
                              <defs>
                                <linearGradient
                                  id="scoreGradient"
                                  x1="0%"
                                  y1="0%"
                                  x2="100%"
                                  y2="0%"
                                >
                                  <stop offset="0%" stopColor="#22c55e" />
                                  <stop offset="100%" stopColor="#16a34a" />
                                </linearGradient>
                              </defs>
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="text-center">
                                <div className="text-2xl font-black text-gray-900">
                                  87
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-base font-bold text-gray-900">
                                Excellent
                              </span>
                              <span className="px-2 py-1 bg-grass-100 rounded text-xs font-bold text-grass-700 flex items-center gap-1">
                                <svg
                                  className="w-3 h-3"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                +5
                              </span>
                            </div>
                            <div className="text-sm text-gray-500 mt-1">
                              Lawn Health Score
                            </div>
                            <div className="flex gap-1.5 mt-2">
                              <div className="h-1.5 flex-1 rounded-full bg-grass-500" />
                              <div className="h-1.5 flex-1 rounded-full bg-grass-500" />
                              <div className="h-1.5 flex-1 rounded-full bg-grass-500" />
                              <div className="h-1.5 flex-1 rounded-full bg-gray-200" />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Weather & Quick Stats Row */}
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="bg-white rounded-xl p-3 shadow-sm text-center">
                          <svg
                            className="w-7 h-7 mx-auto text-amber-500 mb-1"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <div className="text-lg font-bold text-gray-900">
                            72°
                          </div>
                          <div className="text-xs text-gray-500">Sunny</div>
                        </div>
                        <div className="bg-white rounded-xl p-3 shadow-sm text-center">
                          <svg
                            className="w-7 h-7 mx-auto text-sky-500 mb-1"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M5.05 3.636a1 1 0 010 1.414 7 7 0 000 9.9 1 1 0 11-1.414 1.414 9 9 0 010-12.728 1 1 0 011.414 0zm9.9 0a1 1 0 011.414 0 9 9 0 010 12.728 1 1 0 11-1.414-1.414 7 7 0 000-9.9 1 1 0 010-1.414zM7.879 6.464a1 1 0 010 1.414 3 3 0 000 4.243 1 1 0 11-1.415 1.414 5 5 0 010-7.07 1 1 0 011.415 0zm4.242 0a1 1 0 011.415 0 5 5 0 010 7.072 1 1 0 01-1.415-1.415 3 3 0 000-4.242 1 1 0 010-1.415zM10 9a1 1 0 011 1v.01a1 1 0 11-2 0V10a1 1 0 011-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <div className="text-lg font-bold text-gray-900">
                            45%
                          </div>
                          <div className="text-xs text-gray-500">Humidity</div>
                        </div>
                        <div className="bg-white rounded-xl p-3 shadow-sm text-center">
                          <svg
                            className="w-7 h-7 mx-auto text-blue-500 mb-1"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <div className="text-lg font-bold text-gray-900">
                            2d
                          </div>
                          <div className="text-xs text-gray-500">Next Rain</div>
                        </div>
                      </div>

                      {/* Today's Tasks */}
                      <div className="bg-white rounded-2xl shadow-lg p-4 mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-bold text-gray-900">
                            Today&apos;s Tasks
                          </span>
                          <span className="text-xs text-grass-600 font-medium">
                            See All
                          </span>
                        </div>
                        {/* Task 1 */}
                        <div className="flex items-center gap-3 py-2 border-b border-gray-100">
                          <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center flex-shrink-0">
                            <svg
                              className="w-5 h-5 text-sky-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
                              />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">
                              Water lawn
                            </div>
                            <div className="text-xs text-gray-500">
                              Best time: 6:00 AM
                            </div>
                          </div>
                          <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
                        </div>
                        {/* Task 2 */}
                        <div className="flex items-center gap-3 py-2">
                          <div className="w-10 h-10 rounded-xl bg-grass-100 flex items-center justify-center flex-shrink-0">
                            <svg
                              className="w-5 h-5 text-grass-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 5.758a3 3 0 10-4.243 4.243 3 3 0 004.243-4.243zm0-5.758a3 3 0 10-4.243-4.243 3 3 0 004.243 4.243z"
                              />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">
                              Mow at 3.5&quot;
                            </div>
                            <div className="text-xs text-gray-500">
                              Due this weekend
                            </div>
                          </div>
                          <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
                        </div>
                      </div>

                      {/* AI Tip Card */}
                      <div className="bg-gradient-to-r from-grass-500 to-grass-600 rounded-2xl p-4 flex items-center gap-3">
                        <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                          <svg
                            className="w-6 h-6 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-white/80 font-medium">
                            AI TIP
                          </div>
                          <div className="text-sm text-white font-semibold">
                            Water early to reduce evaporation
                          </div>
                        </div>
                        <svg
                          className="w-5 h-5 text-white/60 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </div>
                    </div>

                    {/* Bottom Navigation */}
                    <div className="bg-white border-t border-gray-100 px-6 py-3 flex items-center justify-around">
                      <div className="flex flex-col items-center">
                        <svg
                          className="w-6 h-6 text-grass-600"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                        </svg>
                        <span className="text-[10px] font-semibold text-grass-600 mt-1">
                          Home
                        </span>
                      </div>
                      <div className="flex flex-col items-center">
                        <svg
                          className="w-6 h-6 text-gray-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-[10px] text-gray-400 mt-1">
                          Scan
                        </span>
                      </div>
                      <div className="flex flex-col items-center">
                        <svg
                          className="w-6 h-6 text-gray-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-[10px] text-gray-400 mt-1">
                          Plan
                        </span>
                      </div>
                      <div className="flex flex-col items-center">
                        <svg
                          className="w-6 h-6 text-gray-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-[10px] text-gray-400 mt-1">
                          Chat
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Home indicator */}
                <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-32 h-1 bg-gray-600 rounded-full" />
              </div>

              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 z-20 w-20 h-20 bg-sunset-400 rounded-2xl shadow-xl flex items-center justify-center transform rotate-12 animate-float">
                <svg
                  className="w-10 h-10 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <div
                className="absolute -bottom-4 -left-4 z-20 w-16 h-16 bg-grass-500 rounded-2xl shadow-xl flex items-center justify-center transform -rotate-12 animate-float"
                style={{ animationDelay: '1s' }}
              >
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Hero;
